// Task #2909 — Shared list of common US IANA timezones for the account-level
// timezone picker and any feature that displays/labels a timezone. IANA names
// (not fixed offsets) so scheduled-feature crons handle DST automatically.
// Extracted from the smart-locks settings panel (Task #2898) so the account
// settings page, smart-locks page, and service-form schedules all share one
// canonical list and label map.

export interface TimezoneOption {
  value: string;
  label: string;
}

export const ACCOUNT_TIMEZONES: TimezoneOption[] = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Phoenix", label: "Arizona (MST, no DST)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HST)" },
];

export const DEFAULT_ACCOUNT_TIMEZONE = "America/Chicago";

/** Human-readable label for an IANA timezone, falling back to the raw name. */
export function timezoneLabel(tz: string | null | undefined): string {
  if (!tz) return DEFAULT_ACCOUNT_TIMEZONE;
  return ACCOUNT_TIMEZONES.find((t) => t.value === tz)?.label ?? tz;
}

/**
 * Task #2909 — Resolve the timezone a service-form schedule should use.
 *
 * A stored per-schedule timezone acts as an explicit override and always wins;
 * when a schedule has no stored timezone (a brand-new schedule, or an empty
 * value) it defaults to the host's account-level timezone, finally falling
 * back to the platform default if the account timezone is somehow unset.
 *
 * Both the create and edit schedule dialogs share this resolver so "new
 * schedules default to the account zone, existing schedules keep their stored
 * zone as an override" stays a single, testable rule.
 */
export function resolveScheduleTimezone(
  storedTimezone: string | null | undefined,
  accountTimezone: string | null | undefined,
): string {
  return storedTimezone || accountTimezone || DEFAULT_ACCOUNT_TIMEZONE;
}
